import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  Vector3,
} from 'three'
import type { RoadSpline } from '../core/spline/splineGenerator'
import { convertPosition } from '../core/unity'

/**
 * Placeholder road: a flat ribbon swept along the spline.
 *
 * The shipped game extrudes `Road.obj` along the spline with Dreamteck's
 * SplineMesh. That tile measures 6.5 wide (x −3.25…3.25), 4 long, and 0.029…
 * 0.330 tall, so the ribbon below matches its width and top surface height and
 * will be swapped for the real extrusion in phase 3.
 */
export const ROAD_WIDTH = 6.5
export const ROAD_SURFACE_Y = 0.33

export interface RoadTuning {
  /** World units between ribbon cross-sections. */
  segmentLength: number
}

export const DEFAULT_ROAD_TUNING: RoadTuning = { segmentLength: 0.5 }

const UP = new Vector3(0, 1, 0)

export interface RoadMesh {
  object: Object3D
  dispose(): void
}

export function buildRoadMesh(
  road: RoadSpline,
  tuning: RoadTuning = DEFAULT_ROAD_TUNING,
): RoadMesh {
  const { spline, originalStartPercent, originalEndPercent } = road

  const startDistance = spline.distanceAt(originalStartPercent)
  const endDistance = spline.distanceAt(originalEndPercent)
  const span = endDistance - startDistance
  const steps = Math.max(2, Math.ceil(span / tuning.segmentLength))

  const positions = new Float32Array((steps + 1) * 2 * 3)
  const normals = new Float32Array((steps + 1) * 2 * 3)
  const uvs = new Float32Array((steps + 1) * 2 * 2)
  const indices: number[] = []

  const pos = new Vector3()
  const tangent = new Vector3()
  const side = new Vector3()
  const half = ROAD_WIDTH / 2

  for (let i = 0; i <= steps; i++) {
    const distance = startDistance + (span * i) / steps
    const percent = spline.percentAtDistance(distance)
    spline.evaluatePosition(percent, pos)
    spline.evaluateTangent(percent, tangent)

    // Keep the ribbon flat: the road never banks in this game.
    tangent.y = 0
    if (tangent.lengthSq() < 1e-12) tangent.set(0, 0, 1)
    tangent.normalize()
    side.crossVectors(UP, tangent).normalize()

    convertPosition(pos, pos)
    convertPosition(side, side)

    const base = i * 2
    for (const [k, sign] of [
      [0, -1],
      [1, 1],
    ] as const) {
      const o = (base + k) * 3
      positions[o] = pos.x + side.x * half * sign
      positions[o + 1] = ROAD_SURFACE_Y
      positions[o + 2] = pos.z + side.z * half * sign
      normals[o] = 0
      normals[o + 1] = 1
      normals[o + 2] = 0
      const u = (base + k) * 2
      uvs[u] = k
      uvs[u + 1] = distance / 4 // Road.obj tiles every 4 units.
    }

    if (i > 0) {
      const a = (i - 1) * 2
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2)
    }
  }

  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  geometry.setAttribute('normal', new BufferAttribute(normals, 3))
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2))
  geometry.setIndex(indices)
  geometry.computeBoundingSphere()

  const material = new MeshLambertMaterial({ color: new Color(0x4a4a52) })
  const mesh = new Mesh(geometry, material)
  mesh.name = 'road'
  mesh.receiveShadow = true

  const object = new Object3D()
  object.name = 'roadGroup'
  object.add(mesh)

  return {
    object,
    dispose() {
      geometry.dispose()
      material.dispose()
    },
  }
}

// The spline debug overlay lives in `splineDebug.ts` — it also draws the clip
// range, the control points and each merging bus's entry curve.
